import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { verifyPayment } from '@/services/api/payments';
import { useAuthStore } from '@/features/auth/store';
import { usePaperTheme } from '@/hooks/usePaperTheme';
import type { Payment } from '@/types/payments';

type Phase = 'verifying' | 'success' | 'failed' | 'not_found';

export function BillingReturnPage() {
  usePaperTheme();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const user = useAuthStore((s) => s.user);

  // Paystack sends either `reference` or `trxref`; our mock returns `reference`.
  const reference = params.get('reference') || params.get('trxref') || '';
  // Polar doesn't echo `checkout_id` on custom success_url, so we read the
  // ID from sessionStorage (stashed at initialize time). URL wins if the
  // provider ever does append it — keeps the door open for future changes.
  const polarCheckoutId = (() => {
    const fromUrl = params.get('checkout_id');
    if (fromUrl) return fromUrl;
    try {
      const raw = sessionStorage.getItem('clyra-pending-checkout');
      if (!raw) return undefined;
      const parsed = JSON.parse(raw) as { reference: string; external_id: string; provider: string };
      if (parsed.reference === reference && parsed.provider === 'polar' && parsed.external_id) {
        return parsed.external_id;
      }
    } catch {
      /* ignore */
    }
    return undefined;
  })();

  const [phase, setPhase] = useState<Phase>('verifying');
  const [payment, setPayment] = useState<Payment | null>(null);

  useEffect(() => {
    if (!reference) {
      setPhase('not_found');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const p = await verifyPayment(reference, polarCheckoutId);
        if (cancelled) return;
        setPayment(p);
        if (p.status === 'paid') {
          setPhase('success');
          // Clear the stash now that we've reconciled.
          try {
            sessionStorage.removeItem('clyra-pending-checkout');
          } catch {
            /* ignore */
          }
          if (user) {
            setUser({
              ...user,
              plan: p.plan,
              plan_expiry: plannedExpiry(p),
            });
          }
        } else {
          setPhase('failed');
        }
      } catch {
        if (!cancelled) setPhase('not_found');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-clyra-bg px-4">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,_rgba(30,170,255,0.06),_transparent)]" />

      <div className="relative w-full max-w-md">
        <div className="rounded-clyra-lg border border-line-default bg-clyra-raised/50 backdrop-blur-sm p-8 text-center shadow-clyra">
          {phase === 'verifying' && (
            <>
              <Spinner />
              <h1 className="mt-4 text-base font-semibold text-ink-primary">Confirming your payment…</h1>
              <p className="mt-1 text-petit text-ink-muted">This only takes a second.</p>
            </>
          )}

          {phase === 'success' && payment && (
            <>
              <CheckIcon />
              <h1 className="mt-4 text-base font-semibold text-ink-primary">You're on {planLabel(payment.plan)}.</h1>
              <p className="mt-1 text-petit text-ink-muted">
                Thanks for the upgrade. Your new daily limit kicks in right away.
              </p>
              <div className="mt-6 flex justify-center gap-2">
                <Button size="sm" onClick={() => navigate('/dashboard')}>
                  Back to dashboard
                </Button>
                <Button size="sm" variant="secondary" onClick={() => navigate('/settings')}>
                  View billing
                </Button>
              </div>
            </>
          )}

          {phase === 'failed' && (
            <>
              <WarnIcon />
              <h1 className="mt-4 text-base font-semibold text-ink-primary">Payment didn't go through.</h1>
              <p className="mt-1 text-petit text-ink-muted">
                No charges were captured. You can try again from settings.
              </p>
              <div className="mt-6 flex justify-center gap-2">
                <Button size="sm" onClick={() => navigate('/settings')}>
                  Try again
                </Button>
                <Link to="/dashboard" className="self-center text-petit text-ink-muted hover:text-ink-primary">
                  Back to dashboard
                </Link>
              </div>
            </>
          )}

          {phase === 'not_found' && (
            <>
              <WarnIcon />
              <h1 className="mt-4 text-base font-semibold text-ink-primary">We couldn't find that payment.</h1>
              <p className="mt-1 text-petit text-ink-muted">
                The link may have expired. Start a new upgrade from settings.
              </p>
              <div className="mt-6 flex justify-center">
                <Button size="sm" onClick={() => navigate('/settings')}>
                  Go to settings
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function plannedExpiry(p: Payment): string {
  if (p.verified_at) {
    const d = new Date(p.verified_at);
    d.setDate(d.getDate() + p.period_days);
    return d.toISOString();
  }
  const d = new Date();
  d.setDate(d.getDate() + p.period_days);
  return d.toISOString();
}

function planLabel(plan: string): string {
  if (plan === 'pro') return 'Pro';
  if (plan === 'byo') return 'BYO';
  return plan;
}

function Spinner() {
  return (
    <svg className="mx-auto h-6 w-6 animate-spin text-brand-400" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-brand-500/15 text-brand-300">
      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
      </svg>
    </div>
  );
}

function WarnIcon() {
  return (
    <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-danger/15 text-danger">
      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v4a1 1 0 102 0V7zm-1 7a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
      </svg>
    </div>
  );
}
